import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Inbox, RefreshCcw, CheckCircle2, XCircle, ArrowRight, Link as LinkIcon, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Inbound-email inbox — new v1.10.27 surface.
 *
 * Displays every message the Graph poller has pulled from the shared
 * ``provision@`` mailbox: matched replies (auto-pushed to a Syncro
 * ticket) AND unmatched senders that need admin assignment. The tab
 * filter is client-side because the collection is capped at 200 rows
 * per fetch — plenty for a busy day.
 */
export default function AdminInboundInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | matched | unmatched | dismissed
  const [assigning, setAssigning] = useState(null); // internet_message_id being assigned
  const [clientIdInput, setClientIdInput] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/inbound/inbox");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load inbox");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function pollNow() {
    try {
      const { data } = await api.post("/commissions/inbound/poll-now");
      const processed = data.processed ?? 0;
      const matched = data.matched ?? 0;
      if (data.skipped) toast.info(`Poll skipped — ${data.skipped}`);
      else if (data.error) toast.error(`Poll error: ${data.error}`);
      else toast.success(`Polled ${data.mailbox} — ${processed} new, ${matched} matched`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Poll failed");
    }
  }

  async function assign(row) {
    const cid = clientIdInput.trim();
    if (!cid) { toast.error("Paste a client ID from the Kanban card URL"); return; }
    try {
      const enc = encodeURIComponent(row.internet_message_id);
      const { data } = await api.post(`/commissions/inbound/inbox/${enc}/assign`, { client_id: cid });
      toast.success(data.syncro_comment_pushed ? "Assigned + Syncro comment pushed" : "Assigned");
      setAssigning(null); setClientIdInput("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assign failed");
    }
  }

  async function dismiss(row) {
    try {
      const enc = encodeURIComponent(row.internet_message_id);
      await api.post(`/commissions/inbound/inbox/${enc}/dismiss`);
      toast.success("Dismissed");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Dismiss failed");
    }
  }

  const filtered = rows.filter((r) => tab === "all" ? true : r.status === tab);
  const counts = {
    all: rows.length,
    matched: rows.filter(r => r.status === "matched").length,
    unmatched: rows.filter(r => r.status === "unmatched").length,
    dismissed: rows.filter(r => r.status === "dismissed").length,
  };

  return (
    <div className="space-y-5" data-testid="admin-inbound-inbox">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight flex items-center gap-3"><Inbox className="w-7 h-7 text-cyan-400" /> Inbound inbox</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Emails pulled from your shared <code className="text-slate-300">provision@</code> mailbox. Matched replies auto-post to the Syncro ticket as a public comment; unmatched senders wait here for you to assign.
          </p>
        </div>
        <button data-testid="inbound-poll-now" onClick={pollNow} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-950 text-slate-200 text-sm font-medium">
          <RefreshCcw className="w-3.5 h-3.5" /> Poll now
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="inbound-tabs">
        {[
          {k: "all", label: "All"},
          {k: "matched", label: "Matched"},
          {k: "unmatched", label: "Needs assign"},
          {k: "dismissed", label: "Dismissed"},
        ].map(t => (
          <button key={t.k} data-testid={`inbound-tab-${t.k}`} onClick={() => setTab(t.k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    tab === t.k ? "bg-cyan-500 text-white border-cyan-500" : "border-slate-800 text-slate-300 hover:bg-slate-950"
                  }`}>
            {t.label} <span className="opacity-60 ml-1">({counts[t.k]})</span>
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-950">
            <tr>
              <th className="text-left px-4 py-3">Received</th>
              <th className="text-left px-4 py-3">From</th>
              <th className="text-left px-4 py-3">Subject</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800" data-testid="inbound-tbody">
            {loading && <tr><td colSpan={5} className="text-center py-8 text-slate-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-500">
                {tab === "unmatched" ? "No inbound emails waiting to be assigned." : "No inbound emails yet."}
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.internet_message_id} className="hover:bg-slate-950/50 align-top" data-testid={`inbound-row-${r.internet_message_id}`}>
                <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{(r.received_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-200">{r.from_address}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-100 text-sm">{r.subject || "(no subject)"}</div>
                  <div className="text-[11px] text-slate-500 mt-1 line-clamp-2 max-w-xl">{r.body_text || ""}</div>
                  {(r.auto_added_ccs || []).length > 0 && (
                    <div className="text-[10px] text-cyan-400 mt-1.5 font-mono">+ auto-added to partner CCs: {r.auto_added_ccs.join(", ")}</div>
                  )}
                  {r.matched_client_id && (
                    <div className="text-[10px] text-slate-400 mt-1.5 inline-flex items-center gap-1">
                      <LinkIcon className="w-3 h-3" /> Client: <code className="text-slate-300">{r.matched_client_id}</code>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {r.status === "matched" && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-400 border border-emerald-900"><CheckCircle2 className="w-3 h-3" /> Matched{r.syncro_comment_pushed ? " · Syncro" : ""}</span>}
                  {r.status === "unmatched" && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-950 text-amber-300 border border-amber-900">Needs assign</span>}
                  {r.status === "dismissed" && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Dismissed</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.status === "unmatched" && assigning === r.internet_message_id && (
                    <div className="inline-flex items-center gap-1">
                      <input
                        data-testid={`inbound-clientid-${r.internet_message_id}`}
                        value={clientIdInput}
                        onChange={(e) => setClientIdInput(e.target.value)}
                        placeholder="client_id"
                        className="w-40 rounded-md border border-slate-700 bg-slate-950 text-slate-100 px-2 py-1 text-xs font-mono outline-none focus:border-cyan-500"
                      />
                      <button data-testid={`inbound-assign-confirm-${r.internet_message_id}`} onClick={() => assign(r)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-cyan-500 hover:bg-cyan-400 text-white">
                        <ArrowRight className="w-3 h-3" /> Assign
                      </button>
                      <button onClick={() => { setAssigning(null); setClientIdInput(""); }} className="text-xs text-slate-400 hover:text-slate-200 px-1">×</button>
                    </div>
                  )}
                  {r.status === "unmatched" && assigning !== r.internet_message_id && (
                    <div className="inline-flex gap-1">
                      <button data-testid={`inbound-assign-${r.internet_message_id}`} onClick={() => { setAssigning(r.internet_message_id); setClientIdInput(""); }} className="text-xs text-cyan-400 hover:text-cyan-300 font-medium px-1.5">Assign</button>
                      <button data-testid={`inbound-dismiss-${r.internet_message_id}`} onClick={() => dismiss(r)} className="text-xs text-slate-400 hover:text-rose-400 font-medium px-1.5 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Dismiss</button>
                    </div>
                  )}
                  {r.status === "matched" && r.matched_client_id && (
                    <Link to={`/admin/onboarding?open=${r.matched_client_id}`} className="text-xs text-cyan-400 hover:text-cyan-300 font-medium inline-flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Open card
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
